import { useCallback, useMemo } from "react";
import moment from "moment-timezone";
import { useScheduleContext } from "../hooks/context/useScheduleContext";
import { useIsTemplate } from "../hooks/useIsTemplate";
import { useFilters } from "../../../../utils/hooks/useFilters";
import useApi from "../../../../utils/api";
import { getGroupedResources } from "../helpers/data/getGroupedResources";
import { processUnavailableBlocks } from "../helpers/data/calculateUnavailableEvents";
import { formatTimeSheetParams } from "../../../TimeSheet/api/helpers";
import { formatEvents } from "../helpers/data/formatEvents";
import { formatTemplateEvents } from "../helpers/data/formatTemplateEvents";
import { isCancelError } from "../../../../utils/helpers/errors";

export const useScheduleApi = () => {
    const [filters] = useFilters();
    const { authGet } = useApi();
    const { dispatch, type, template, templates } = useScheduleContext();
    const isTemplate = useIsTemplate();
    const userTimezone = moment.tz.guess(true);

    const activeTemplateId = filters?.template?.id || template?.id || templates?.[0]?.id;

    const url = useMemo(() => {
        switch (type) {
            default:
            case "team": {
                return "/schedules/v2"
            }
            case "home": {
                return "/my/schedules/v2";
            }
            case "template":
            case "profile": {
                return `/shift-templates/v2/${activeTemplateId}`;
            }
            case "my-profile": {
                return `/my/shift-templates/v2/${activeTemplateId}`;
            }
        }
    }, [type, activeTemplateId]);

    const fetchData = useCallback(async (currentFilters: any, controller: any) => {
        dispatch({ type: "get-data" });

        try {
            const isMonth = currentFilters?.mode === "month";
            const isPaged = type === "team" || type === "template";
            const baseParams: any = {
                filters: currentFilters?.filters ?? [],
                sort: currentFilters?.sort,
            };

            if (!isTemplate) {
                baseParams.startDate = isMonth
                    ? moment(currentFilters?.from).startOf("week").format("YYYY-MM-DD")
                    : moment(currentFilters?.from).format("YYYY-MM-DD");

                baseParams.endDate = isMonth
                    ? moment(currentFilters?.to).endOf("week").format("YYYY-MM-DD")
                    : moment(currentFilters?.to).format("YYYY-MM-DD");
            } else {
                baseParams.mode = currentFilters?.mode;
                baseParams.day = currentFilters?.day;
            }

            if (isPaged) {
                baseParams.page = Number(currentFilters?.page || 1) - 1;
                baseParams.perPage = currentFilters?.pageSize || 50;
            }

            const response = await authGet(url, {
                params: {
                    ...formatTimeSheetParams(baseParams),
                    monthly: isMonth,
                    userId: currentFilters.user
                },
                signal: controller?.signal
            });

            if (!response || controller?.signal?.aborted) return;

            const { events = [], resources = [] } = response.data || {};
            const viewBy = (isMonth || !currentFilters?.viewBy) ? "name" : currentFilters?.viewBy;
            const groupedResources = getGroupedResources(resources, viewBy);

            // One block per row, so a user grouped under several rows shows it on each of them
            const unavailable = groupedResources
                ?.filter((resource: any) => !resource?.isHeader)
                ?.flatMap((resource: any) =>
                    resource?.intervals
                        ?.map((i: any) => ({
                            ...i,
                            id: i.id && resource?.userId ? `${i.id}_${resource.id}` : i.id,
                            user: resource,
                            resourceId: resource?.id
                        })) ?? []);

            const formattedEvents = formatEvents(events);
            const dispatchType = isMonth ? "set-calendar-events" : "get-data-success";

            const _events = isTemplate
                ? formatTemplateEvents(events, currentFilters.mode)
                : [
                    ...formattedEvents,
                    ...(currentFilters.mode === "day" ? processUnavailableBlocks(unavailable) : [])
                ];

            let payload = {
                allResources: groupedResources,
                resources: groupedResources,
                totalItems: response.meta?.total,
                events: _events,
                unavailable: processUnavailableBlocks(unavailable)
            };

            if (isMonth) { payload = response; }

            dispatch({ type: dispatchType, payload });
            const clearType = !isMonth ? "set-calendar-events" : "get-data-success";
            let clearPayload: any = {
                allResources: [],
                resources: [],
                totalItems: 0,
                events: [],
            }
            if (!isMonth) { clearPayload = {} };
            dispatch({ type: clearType, payload: clearPayload })
        } catch (error) {
            if (isCancelError(error) || controller?.signal?.aborted) return;
            dispatch({ type: "get-data-failed", payload: { error } });
        }
    }, [authGet, url, type, isTemplate, dispatch]);

    const getUnassignedShifts = useCallback(async (filters: any, controller?: AbortController) => {
        dispatch({ type: "get-unassigned-events" });

        const params: any = {
            filters: filters?.filters || [],
            startDate: filters?.mode === "month"
                ? moment(filters?.from).startOf("week").format("YYYY-MM-DD")
                : moment(filters.from).format("YYYY-MM-DD"),
            endDate: filters?.mode === "month"
                ? moment(filters?.to).endOf("week").format("YYYY-MM-DD")
                : moment(filters.to).format("YYYY-MM-DD"),
        };

        try {
            const response = await authGet("/unassigned-shifts", {
                params: formatTimeSheetParams(params),
                signal: controller?.signal
            });
            if (response && !controller?.signal?.aborted) {
                const events = response.map((event: any) => ({
                    ...event,
                    resourceId: "unassigned-shifts",
                    id: event.id,
                    startDate: moment.parseZone(event.startDate).tz(userTimezone, true),
                    endDate: moment.parseZone(event.endDate).tz(userTimezone, true),
                }));
                dispatch({
                    type: "get-unassigned-events-success",
                    payload: events
                })
            }
        } catch (err) {
            if (isCancelError(err) || controller?.signal?.aborted) return;
            dispatch({ type: "get-unassigned-events-failed", payload: { error: err } });
        }
    }, [dispatch, authGet, userTimezone]);

    return { fetchData, getUnassignedShifts };
};
